/**
 * Vault Hire AppShell — uniform global shell wrapping every authenticated route.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ TopBar (logo, module switcher, accessibility, user menu)     │
 *   ├──────────────┬───────────────────────────────────────────────┤
 *   │ LeftRail     │ <main id="main-content">                      │
 *   │ (sections)   │   {children}                                  │
 *   │              │                                               │
 *   └──────────────┴───────────────────────────────────────────────┘
 *
 * Design: "Calm vault with warm trust accents" — deep pine, sand neutrals,
 * coral CTA. Per /app/design_guidelines.json.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronDown, Bell, Search, Accessibility, Menu, X, Lock, LogOut, KeyRound,
  User as UserIcon, Sliders, ShieldCheck,
} from 'lucide-react';

import { MODULES_BY_ROLE, activeModuleForPath } from './shellConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* =========================================================================
   useAccessibilitySettings — persists font size / contrast / dyslexia font
   to localStorage and applies them to <html> via data attributes consumed by
   global CSS in index.css.
   ========================================================================= */
const A11Y_KEY = 'vh-a11y-prefs';

function loadA11y() {
  try {
    return JSON.parse(localStorage.getItem(A11Y_KEY)) || {};
  } catch { return {}; }
}

function applyA11y(prefs) {
  const html = document.documentElement;
  html.style.setProperty('--vh-text-scale', String(prefs.textScale || 1));
  if (prefs.contrast === 'high') html.setAttribute('data-vh-contrast', 'high');
  else html.removeAttribute('data-vh-contrast');
  if (prefs.font === 'dyslexia') html.setAttribute('data-vh-font', 'dyslexia');
  else html.removeAttribute('data-vh-font');
}

function useAccessibility() {
  const [prefs, setPrefs] = useState(() => {
    const p = loadA11y();
    applyA11y(p);
    return p;
  });
  const update = (next) => {
    const merged = { ...prefs, ...next };
    setPrefs(merged);
    localStorage.setItem(A11Y_KEY, JSON.stringify(merged));
    applyA11y(merged);
  };
  return [prefs, update];
}

/* =========================================================================
   AccessibilityMenu — top-bar dropdown for vision/cognition adjustments.
   ========================================================================= */
export function AccessibilityMenu() {
  const [open, setOpen] = useState(false);
  const [prefs, update] = useAccessibility();

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        className="vh-focusable inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-[var(--vh-bg-muted)] text-[var(--vh-text-secondary)]"
        aria-label="Accessibility settings"
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="accessibility-menu-trigger"
      >
        <Accessibility className="w-5 h-5" />
      </button>
      {open && (
        <>
          <button
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-30 cursor-default"
          />
          <div
            role="menu"
            data-testid="accessibility-menu-panel"
            className="absolute right-0 mt-2 w-72 bg-white border border-[var(--vh-border)] rounded-2xl shadow-[0_8px_32px_rgba(30,63,51,0.08)] z-40 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-[var(--vh-border)]">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--vh-text-muted)]">Accessibility</p>
            </div>

            <div className="px-4 py-3 space-y-3 text-sm">
              <div>
                <p className="font-medium mb-1.5">Text size</p>
                <div className="flex gap-1.5">
                  {[
                    { v: 1, l: 'Default' },
                    { v: 1.125, l: 'Large' },
                    { v: 1.25, l: 'Extra-large' },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => update({ textScale: o.v })}
                      className={`flex-1 px-2 py-1.5 rounded-full text-xs border ${prefs.textScale === o.v ? 'bg-[var(--vh-brand-primary)] text-white border-[var(--vh-brand-primary)]' : 'border-[var(--vh-border)] hover:bg-[var(--vh-bg-muted)]'}`}
                      data-testid={`a11y-text-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-medium mb-1.5">Contrast</p>
                <div className="flex gap-1.5">
                  {[
                    { v: 'default', l: 'Default' },
                    { v: 'high',    l: 'High contrast' },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => update({ contrast: o.v })}
                      className={`flex-1 px-2 py-1.5 rounded-full text-xs border ${(prefs.contrast || 'default') === o.v ? 'bg-[var(--vh-brand-primary)] text-white border-[var(--vh-brand-primary)]' : 'border-[var(--vh-border)] hover:bg-[var(--vh-bg-muted)]'}`}
                      data-testid={`a11y-contrast-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-medium mb-1.5">Font</p>
                <div className="flex gap-1.5">
                  {[
                    { v: 'default',  l: 'Default' },
                    { v: 'dyslexia', l: 'Dyslexia-friendly' },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => update({ font: o.v })}
                      className={`flex-1 px-2 py-1.5 rounded-full text-xs border ${(prefs.font || 'default') === o.v ? 'bg-[var(--vh-brand-primary)] text-white border-[var(--vh-brand-primary)]' : 'border-[var(--vh-border)] hover:bg-[var(--vh-bg-muted)]'}`}
                      data-testid={`a11y-font-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-[var(--vh-text-muted)] pt-1">Saved in this browser. Resets with cache clear.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* =========================================================================
   ModuleSwitcher — top-bar dropdown showing the role's modules.
   ========================================================================= */
function ModuleSwitcher({ role, current, onPick }) {
  const [open, setOpen] = useState(false);
  const list = MODULES_BY_ROLE[role] || [];
  if (!current) return null;
  const CurIcon = current.Icon;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        className="vh-focusable inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--vh-bg-muted)] hover:bg-[var(--vh-border)] text-sm font-medium text-[var(--vh-text-primary)]"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="module-switcher-trigger"
      >
        <CurIcon className="w-4 h-4" aria-hidden />
        <span>{current.label}</span>
        <ChevronDown className="w-4 h-4 text-[var(--vh-text-muted)]" aria-hidden />
      </button>
      {open && (
        <>
          <button aria-hidden tabIndex={-1} onClick={() => setOpen(false)} className="fixed inset-0 z-30 cursor-default" />
          <div
            role="menu"
            data-testid="module-switcher-panel"
            className="absolute left-1/2 -translate-x-1/2 mt-2 w-72 bg-white border border-[var(--vh-border)] rounded-2xl shadow-[0_8px_32px_rgba(30,63,51,0.08)] z-40 overflow-hidden py-2"
          >
            {list.map((m) => {
              const Icon = m.Icon;
              const active = m.id === current.id;
              return (
                <button
                  key={m.id}
                  role="menuitem"
                  onClick={() => { setOpen(false); onPick(m); }}
                  className={`w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-[var(--vh-bg-muted)] ${active ? 'font-semibold text-[var(--vh-brand-primary)]' : 'text-[var(--vh-text-primary)]'}`}
                  data-testid={`module-switcher-item-${m.id}`}
                >
                  <Icon className="w-4 h-4" aria-hidden />
                  <span>{m.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* =========================================================================
   UserMenu — avatar with profile / passkeys / logout.
   ========================================================================= */
function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const initials = (user?.full_name || user?.email || '?')
    .split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        className="vh-focusable w-9 h-9 rounded-full bg-[var(--vh-brand-primary)] text-white text-xs font-semibold flex items-center justify-center"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="user-menu-trigger"
      >
        {initials}
      </button>
      {open && (
        <>
          <button aria-hidden tabIndex={-1} onClick={() => setOpen(false)} className="fixed inset-0 z-30 cursor-default" />
          <div
            role="menu"
            data-testid="user-menu-panel"
            className="absolute right-0 mt-2 w-64 bg-white border border-[var(--vh-border)] rounded-2xl shadow-[0_8px_32px_rgba(30,63,51,0.08)] z-40 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-[var(--vh-border)]">
              <p className="text-sm font-semibold truncate">{user?.full_name || 'Vault Hire user'}</p>
              <p className="text-xs text-[var(--vh-text-muted)] truncate">{user?.email}</p>
              <p className="text-[11px] uppercase tracking-widest text-[var(--vh-text-muted)] mt-1">{user?.role}</p>
            </div>
            <button onClick={() => { setOpen(false); navigate('/security/passkeys'); }}
              role="menuitem" className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-[var(--vh-bg-muted)]"
              data-testid="user-menu-passkeys">
              <KeyRound className="w-4 h-4" aria-hidden /> Manage passkeys
            </button>
            <button onClick={() => { setOpen(false); navigate('/candidate/dashboard'); }}
              role="menuitem" className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-[var(--vh-bg-muted)]"
              data-testid="user-menu-profile">
              <UserIcon className="w-4 h-4" aria-hidden /> Profile
            </button>
            <button onClick={() => { setOpen(false); navigate('/settings/ai-preferences'); }}
              role="menuitem" className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-[var(--vh-bg-muted)]"
              data-testid="user-menu-ai-prefs">
              <Sliders className="w-4 h-4" aria-hidden /> AI preferences
            </button>
            {(user?.compliance_role || user?.role === 'owner' || user?.role === 'admin') && (
              <button onClick={() => { setOpen(false); navigate('/compliance-hub'); }}
                role="menuitem" className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-[var(--vh-bg-muted)]"
                data-testid="user-menu-compliance-hub">
                <ShieldCheck className="w-4 h-4" aria-hidden /> Compliance &amp; Security Hub
              </button>
            )}
            <div className="border-t border-[var(--vh-border)]">
              <button onClick={() => { setOpen(false); onLogout(); }}
                role="menuitem" className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-[var(--vh-bg-muted)] text-[var(--vh-accent)]"
                data-testid="user-menu-logout">
                <LogOut className="w-4 h-4" aria-hidden /> Log out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* =========================================================================
   LeftRail — in-module sections.
   ========================================================================= */
function LeftRail({ module: mod, pathname, collapsed, onToggle }) {
  if (!mod || !mod.sections || mod.sections.length === 0) {
    return (
      <aside
        aria-label="Module sections"
        className={`hidden lg:block ${collapsed ? 'w-16' : 'w-64'} flex-shrink-0 border-r border-[var(--vh-border)] bg-[var(--vh-bg-base)] transition-[width] duration-200`}
      >
        <div className="p-4 flex justify-end">
          <button onClick={onToggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="vh-focusable w-8 h-8 rounded-full hover:bg-[var(--vh-bg-muted)] text-[var(--vh-text-muted)] flex items-center justify-center">
            {collapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </button>
        </div>
      </aside>
    );
  }
  return (
    <aside
      aria-label="Module sections"
      data-testid="left-rail"
      className={`hidden lg:block ${collapsed ? 'w-16' : 'w-64'} flex-shrink-0 border-r border-[var(--vh-border)] bg-[var(--vh-bg-base)] transition-[width] duration-200`}
    >
      <div className="p-4 flex items-center justify-between">
        {!collapsed && <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--vh-text-muted)]">{mod.label}</p>}
        <button onClick={onToggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="vh-focusable w-7 h-7 rounded-full hover:bg-[var(--vh-bg-muted)] text-[var(--vh-text-muted)] flex items-center justify-center">
          {collapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
        </button>
      </div>
      <nav className="px-3 pb-6" aria-label="In-module navigation">
        <ul className="space-y-1">
          {mod.sections.map((s) => {
            const Icon = s.Icon;
            const active = pathname === s.path.split('#')[0];
            return (
              <li key={s.id}>
                <Link
                  to={s.path}
                  data-testid={`left-rail-${s.id}`}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${active ? 'bg-white text-[var(--vh-brand-primary)] shadow-[0_4px_24px_rgba(30,63,51,0.04)] font-medium' : 'text-[var(--vh-text-secondary)] hover:bg-[var(--vh-bg-muted)]'}`}
                >
                  <Icon className="w-4 h-4 shrink-0" aria-hidden />
                  {!collapsed && <span className="truncate">{s.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}

/* =========================================================================
   AppShell — public component.
   ========================================================================= */
export default function AppShell({ user, onLogout, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const role = user?.role;
  const current = useMemo(() => activeModuleForPath(role, location.pathname), [role, location.pathname]);
  const [railCollapsed, setRailCollapsed] = useState(false);

  // Re-apply a11y prefs on every shell mount so settings survive reloads.
  useEffect(() => { applyA11y(loadA11y()); }, []);

  // Logout fallback if parent doesn't provide one.
  const handleLogout = onLogout || (async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) {
      // Best-effort logout; cookie may already be cleared. Navigate anyway.
      if (typeof console !== 'undefined') console.debug('logout endpoint failed (ignored):', e?.message || e);
    }
    navigate('/login');
  });

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] text-[var(--vh-text-primary)]">
      <a href="#main-content" className="vh-skip-link">Skip to main content</a>

      <header className="sticky top-0 z-40 h-16 bg-white/80 backdrop-blur-xl border-b border-[var(--vh-border)]">
        <div className="h-full px-4 sm:px-6 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="vh-focusable flex items-center gap-2" data-testid="app-shell-logo">
            <div className="w-8 h-8 rounded-xl bg-[var(--vh-brand-primary)] text-white flex items-center justify-center font-display font-bold text-sm">
              V
            </div>
            <span className="hidden sm:inline font-display font-semibold tracking-tight">Vault Hire</span>
          </Link>

          {/* Module switcher */}
          <div className="flex-1 flex justify-center">
            <ModuleSwitcher role={role} current={current} onPick={(m) => navigate(m.path)} />
          </div>

          {/* Right cluster */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button aria-label="Search" data-testid="app-shell-search"
              className="vh-focusable hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-[var(--vh-bg-muted)] text-[var(--vh-text-secondary)]">
              <Search className="w-5 h-5" />
            </button>
            <button aria-label="Notifications" data-testid="app-shell-notifications"
              className="vh-focusable inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-[var(--vh-bg-muted)] text-[var(--vh-text-secondary)]">
              <Bell className="w-5 h-5" />
            </button>
            <AccessibilityMenu />
            <UserMenu user={user} onLogout={handleLogout} />
          </div>
        </div>
      </header>

      <div className="flex">
        <LeftRail
          module={current}
          pathname={location.pathname}
          collapsed={railCollapsed}
          onToggle={() => setRailCollapsed((s) => !s)}
        />
        <main id="main-content" className="flex-1 min-w-0" data-testid="app-shell-main">
          {children}
        </main>
      </div>
    </div>
  );
}

/* =========================================================================
   PageHeader — uniform page-title pattern with primary CTA slot.
   ========================================================================= */
export function PageHeader({ eyebrow, title, subtitle, actions, children }) {
  return (
    <div className="border-b border-[var(--vh-border)] bg-[var(--vh-bg-surface)]">
      <div className="px-6 lg:px-10 py-8 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--vh-text-muted)] mb-1">{eyebrow}</p>}
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[var(--vh-text-primary)]">{title}</h1>
          {subtitle && <p className="text-sm text-[var(--vh-text-secondary)] mt-1 max-w-2xl">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
      {children && <div className="px-6 lg:px-10 pb-2">{children}</div>}
    </div>
  );
}
